import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import PlayTrailer from '@jwp/ott-theme/assets/icons/play_trailer.svg?react';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useMovieData from '@jwp/ott-hooks-react/src/useMovieData';
import useMovieParams from '@jwp/ott-ui-react/src/hooks/useMovieParams';
import useMovieHandlers from '@jwp/ott-ui-react/src/hooks/useMovieHandlers';
import useTrailerData from '@jwp/ott-hooks-react/src/useTrailerData';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import Button from '@jwp/ott-ui-react/src/components/Button/Button';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import VideoDetailsInline from '@jwp/ott-ui-react/src/components/VideoDetailsInline/VideoDetailsInline';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import MediaInlineLayout from '@jwp/ott-ui-react/src/components/MediaInlineLayout/MediaInlineLayout';
import env from '@jwp/ott-common/src/env';
import MediaSection from '@jwp/ott-ui-react/src/components/MediaSection/MediaSection';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import VideoList from '@jwp/ott-ui-react/src/components/VideoList/VideoList';
import { generateMovieJSONLD } from '@jwp/ott-common/src/utils/structuredData';

import InlinePlayer from '../../../../containers/InlinePlayer/InlinePlayer';

import styles from './MediaVideoInline.module.scss';

const MediaVideoInline: ScreenComponent<PlaylistItem> = ({ data, isLoading }) => {
  const { t } = useTranslation('video');
  const { id, play, feedId } = useMovieParams();
  const {
    accessModel,
    isLoggedIn,
    hasSubscription,
    playlist,
    playlistTitle,
    nextItem,
    movieURL,
    mediaOffers,
    isPlaylistLoading,
    isFavoritesEnabled,
    isSharingEnabled,
    isEntitled,
    primaryMetadata,
    getCardUrl,
  } = useMovieData(data, id, feedId);
  const { handleComplete } = useMovieHandlers(nextItem, movieURL, playlist);
  const { isTrailerPlaying, hasTrailer, trailerItem, trailerTitle, closeTrailer, playTrailer } = useTrailerData(data);

  const structuredData = generateMovieJSONLD(data, env.APP_PUBLIC_URL);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;

  useAutoFocus(id);

  // inline layout specific
  const breakpoint = useBreakpoint();
  const isTablet = breakpoint === Breakpoint.sm || breakpoint === Breakpoint.md;

  const startWatchingButton = (
    <StartWatchingButton
      key={id} // necessary to fix autofocus on TalkBack
      item={data}
      playUrl={mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: true })}
    />
  );

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} structuredData={structuredData} />
      <MediaInlineLayout
        player={
          <InlinePlayer
            isLoggedIn={isLoggedIn}
            item={data}
            onComplete={handleComplete}
            feedId={feedId ?? undefined}
            startWatchingButton={startWatchingButton}
            isEntitled={isEntitled}
            hasMediaOffers={!!mediaOffers.length}
            autostart={play || undefined}
          />
        }
        videoDetails={
          <VideoDetailsInline
            title={data.title}
            live={data?.duration === 0}
            description={data.description}
            primaryMetadata={<VideoMetaData attributes={primaryMetadata} />}
            shareButton={isSharingEnabled && <ShareButton title={data.title} description={data.description} url={canonicalUrl} />}
            favoriteButton={isFavoritesEnabled && <FavoriteButton item={data} />}
            trailerButton={
              hasTrailer && (
                <Button
                  label={t('video:trailer')}
                  aria-label={t('video:watch_trailer')}
                  startIcon={<Icon icon={PlayTrailer} />}
                  onClick={playTrailer}
                  active={isTrailerPlaying}
                  fullWidth={breakpoint < Breakpoint.md}
                  disabled={!trailerItem}
                />
              )
            }
          />
        }
      >
        {playlist && (
          <MediaSection title={playlistTitle || '\u00A0'} className={styles.relatedVideos}>
            {isTablet ? (
              <CardGrid
                playlist={playlist}
                isLoading={isLoading || isPlaylistLoading}
                accessModel={accessModel}
                isLoggedIn={isLoggedIn}
                currentCardItem={data}
                currentCardLabel={t('video:current_video')}
                hasSubscription={hasSubscription}
                getUrl={getCardUrl}
              />
            ) : (
              <VideoList
                activeMediaId={data?.mediaid}
                activeLabel={t('video:current_video')}
                playlist={playlist}
                isLoading={isLoading || isPlaylistLoading}
                accessModel={accessModel}
                isLoggedIn={isLoggedIn}
                hasSubscription={hasSubscription}
                getUrl={getCardUrl}
              />
            )}
          </MediaSection>
        )}
        <TrailerModal item={trailerItem} title={trailerTitle} open={isTrailerPlaying} onClose={closeTrailer} />
      </MediaInlineLayout>
    </React.Fragment>
  );
};

export default MediaVideoInline;
